import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { enrichPOIWithGoogleData } from '../services/googlePlacesService';
import { isSaved, toggleSaved, directionsUrl } from '../services/savedPlacesService';
import { track } from '../services/firebase';
import PlaceNotes from './PlaceNotes';

const MapThumbnail = lazy(() => import('./MapThumbnail'));

/**
 * `showMap` — for a thin place (an unnamed "Picnic area" with no photo and no
 * verified answers), WHERE it is, is the only useful thing we have, and a
 * link out to Google Maps shouldn't be the only way to learn it. Off when the
 * card already sits on top of the map (NearbyMap), where it'd be redundant.
 */
export default function POICard({ poi, onClose, showMap = true, activityId = null }) {
  const [google, setGoogle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  // Mobile: a peek-height sheet you can drag up to read, or fling down to
  // dismiss — you shouldn't have to commit to a place to glance at it.
  // Desktop ignores all of this and renders a normal centred panel.
  const [expanded, setExpanded] = useState(true);
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef(null);
  // The gesture decision reads the delta from a REF, not state: touchend can
  // land in the same tick as the last touchmove, before React re-renders, so
  // reading state there can see a stale 0 and drop the drag entirely.
  const dragDelta = useRef(0);

  useEffect(() => {
    if (poi) setSaved(isSaved(poi.id));
    setExpanded(true);
    setDragY(0);
  }, [poi?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const previous = document.activeElement;
    panelRef.current?.focus();
    function handleKey(event) {
      if (event.key === 'Escape') closeRef.current();
      if (event.key !== 'Tab') return;
      const items = Array.from(panelRef.current?.querySelectorAll('button, a[href], summary, [tabindex="0"]') || [])
        .filter((item) => item.getClientRects().length > 0 && !item.disabled);
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  const onTouchStart = (e) => {
    dragStart.current = e.touches[0].clientY;
    dragDelta.current = 0;
  };
  const onTouchMove = (e) => {
    if (dragStart.current == null) return;
    const delta = e.touches[0].clientY - dragStart.current;
    // Resist upward drag past the expanded stop so it feels anchored.
    const clamped = expanded ? Math.max(0, delta) : Math.max(-90, delta);
    dragDelta.current = clamped;
    setDragY(clamped);
  };
  const onTouchEnd = () => {
    const delta = dragDelta.current;
    dragStart.current = null;
    dragDelta.current = 0;
    setDragY(0);
    if (delta < -50) setExpanded(true);        // dragged up → read it
    else if (delta > 90) {
      if (expanded) setExpanded(false);        // full → peek
      else onClose();                          // peek → gone
    }
  };
  // A real drag also fires a click; don't let it undo what the drag just did.
  const onHandleClick = () => {
    if (Math.abs(dragDelta.current) > 8) return;
    setExpanded((v) => !v);
  };

  useEffect(() => {
    if (!poi) return;
    let active = true;
    setGoogle(null);
    setLoading(true);
    enrichPOIWithGoogleData(poi.id, poi.name, poi.lat, poi.lon)
      .then(data => { if (active) setGoogle(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [poi?.id]);

  if (!poi) return null;

  const typeLabels = {
    park: 'Park',
    trail: 'Trail',
    hike: 'Hiking Trail',
    water: 'Stream Gauge',
    waterfall: 'Waterfall',
    viewpoint: 'Viewpoint',
    swimming: 'Swimming Area',
    fishing: 'Fishing Spot',
    picnic: 'Picnic Area',
  };

  const typeColors = {
    park:      'bg-emerald-50 text-emerald-700 border-emerald-200',
    trail:     'bg-amber-50 text-amber-700 border-amber-200',
    hike:      'bg-orange-50 text-orange-700 border-orange-200',
    water:     'bg-blue-50 text-blue-700 border-blue-200',
    waterfall: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    viewpoint: 'bg-purple-50 text-purple-700 border-purple-200',
    swimming:  'bg-sky-50 text-sky-700 border-sky-200',
    fishing:   'bg-teal-50 text-teal-700 border-teal-200',
    picnic:    'bg-lime-50 text-lime-700 border-lime-200',
  };

  return (
    // Two different surfaces, one component:
    //  - mobile: a bottom sheet anchored low, draggable (peek -> full -> gone)
    //  - sm+: a centred panel. A 50vh sheet pinned to the bottom of a laptop
    //    window means scrolling a keyhole while most of the screen sits empty.
    <div
      className={`absolute inset-x-0 bottom-0 z-[1000] mx-auto max-w-lg animate-slide-up ${
        // On a map, stay a bottom sheet at every size — centring a panel would
        // cover the very thing the user is looking at.
        showMap ? 'sm:inset-0 sm:flex sm:max-w-none sm:items-center sm:justify-center sm:p-6' : ''
      }`}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={poi.name}
        tabIndex={-1}
        style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
        className={`flex flex-col overflow-hidden rounded-t-2xl border-t border-stone-200 bg-white shadow-2xl
                    ${dragY ? '' : 'transition-[max-height,transform] duration-300 ease-out'}
                    ${expanded ? 'max-h-[88vh]' : 'max-h-[50vh]'}
                    ${showMap ? 'sm:mx-auto sm:w-full sm:max-w-xl sm:max-h-[85vh] sm:rounded-2xl sm:border' : ''}`}
      >
        {/* Drag handle — the grab target on mobile; meaningless on desktop */}
        <div
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onClick={onHandleClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setExpanded((value) => !value);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          className="shrink-0 cursor-grab touch-none pt-3 pb-2 active:cursor-grabbing sm:hidden"
        >
          <div className="mx-auto h-1 w-10 rounded-full bg-stone-300" />
        </div>

        <div className="overflow-y-auto overscroll-contain px-4 pb-4 pt-3 sm:pt-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${typeColors[poi.type] || typeColors.trail}`}>
                  {typeLabels[poi.type] || 'Point of Interest'}
                </span>
                <span className="text-xs text-stone-400">
                  {Number.isFinite(poi.distance) ? `${poi.distance.toFixed(1)} mi straight-line` : 'Saved place'}
                </span>
              </div>
              <h3 className="font-semibold text-[#2d3b2d] text-lg leading-tight">
                {poi.name}
              </h3>
              {/* Google rating inline */}
              {google?.rating != null && (
                <div className="flex items-center gap-1.5 mt-1">
                  <StarRating rating={google.rating} />
                  <span className="text-sm font-medium text-stone-700">{google.rating}</span>
                  {google.userRatingCount != null && (
                    <span className="text-xs text-stone-400">({google.userRatingCount.toLocaleString()})</span>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              aria-label="Close details"
              className="p-1 text-stone-300 hover:text-stone-500 transition-colors ml-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Google photo */}
          {google?.photoUri && (
            <div className="mb-3 rounded-xl overflow-hidden">
              <img
                src={google.photoUri}
                alt={poi.name}
                className="w-full h-36 object-cover"
                loading="lazy"
              />
            </div>
          )}

          {/* Where it is — the answer for places we can't say much else about.
              Interactive: a map you can't zoom is a picture of a map. */}
          {showMap && poi.lat != null && (
            <div className="mb-3 h-44 overflow-hidden rounded-xl border border-stone-200 isolate">
              <Suspense fallback={<div className="h-full w-full bg-stone-100" />}>
                <MapThumbnail
                  lat={poi.lat}
                  lon={poi.lon}
                  pois={[poi]}
                  zoom={14}
                  showUser={false}
                  interactive
                />
              </Suspense>
            </div>
          )}

          {/* Actions — the card has to be able to DO something */}
          <div className="mb-3 flex gap-2">
            <button
              onClick={() => {
                const now = toggleSaved(poi, activityId);
                setSaved(now);
                track(now ? 'place_saved' : 'place_unsaved', { place_type: poi.type });
              }}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-all active:scale-[0.98] ${
                saved
                  ? 'border border-[#2d3b2d] bg-[#eef2ec] text-[#2d3b2d]'
                  : 'border border-stone-200 bg-white text-stone-600 hover:border-stone-300'
              }`}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill={saved ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              {saved ? 'Saved' : 'Save'}
            </button>
            <a
              href={directionsUrl(poi)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('place_directions', { place_type: poi.type })}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-b from-[#3a4d3a] to-[#2d3b2d] px-3 py-2.5 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] active:scale-[0.98]"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
              Directions
            </a>
          </div>

          <PlaceNotes place={poi} activityId={activityId || poi.savedFor} />

          {/* Details by type */}
          {poi.type === 'water' && <WaterDetails details={poi.details || {}} />}
          {poi.type === 'park' && <ParkDetails details={poi.details || {}} />}
          {poi.type === 'trail' && <TrailDetails details={poi.details || {}} />}
          {poi.type === 'hike' && <HikeDetails details={poi.details || {}} />}
          {(poi.type === 'waterfall' || poi.type === 'viewpoint' || poi.type === 'swimming' || poi.type === 'fishing' || poi.type === 'picnic') && (
            <FeatureDetails details={poi.details || {}} />
          )}

          {/* Google reviews */}
          {google?.reviews?.length > 0 && (
            <GoogleReviews reviews={google.reviews} />
          )}

          {/* Loading skeleton for Google data */}
          {loading && (
            <div className="mt-3 space-y-2 animate-pulse">
              <div className="h-3 bg-stone-100 rounded w-1/3"></div>
              <div className="h-3 bg-stone-100 rounded w-2/3"></div>
            </div>
          )}

          {/* Google Maps link */}
          {google?.googleMapsUri && (
            <a
              href={google.googleMapsUri}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center gap-1.5 text-sm text-[#2d3b2d] font-medium hover:underline"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              View on Google Maps
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const fill = rating >= i ? 1 : rating >= i - 0.5 ? 0.5 : 0;
    stars.push(
      <svg key={i} className="w-3.5 h-3.5" viewBox="0 0 20 20">
        <defs>
          <linearGradient id={`star-${i}-${rating}`}>
            <stop offset={`${fill * 100}%`} stopColor="#f59e0b" />
            <stop offset={`${fill * 100}%`} stopColor="#d1d5db" />
          </linearGradient>
        </defs>
        <path
          fill={`url(#star-${i}-${rating})`}
          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
        />
      </svg>
    );
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

function GoogleReviews({ reviews }) {
  return (
    <div className="mt-3 space-y-2">
      <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Reviews</h4>
      {reviews.map((review, i) => (
        <div key={i} className="bg-stone-50 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-stone-700">{review.author}</span>
            {review.rating != null && (
              <div className="flex items-center gap-0.5">
                <svg className="w-3 h-3 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <span className="text-xs text-stone-500">{review.rating}</span>
              </div>
            )}
            {review.relativeTime && (
              <span className="text-xs text-stone-400">{review.relativeTime}</span>
            )}
          </div>
          {review.text && (
            <p className="text-xs text-stone-600 leading-relaxed line-clamp-3">{review.text}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function WaterDetails({ details }) {
  return (
    <div className="space-y-2">
      {details.flowRate != null && (
        <div className="flex items-center justify-between bg-blue-50 rounded-xl px-3 py-2">
          <span className="text-sm text-blue-700">Stream Flow</span>
          <span className="text-sm font-semibold text-blue-800">{details.flowRate} {details.flowUnit}</span>
        </div>
      )}
      {details.waterTemp != null && (
        <div className="flex items-center justify-between bg-blue-50 rounded-xl px-3 py-2">
          <span className="text-sm text-blue-700">Water Temp</span>
          <span className="text-sm font-semibold text-blue-800">{details.waterTemp}°C</span>
        </div>
      )}
    </div>
  );
}

function ParkDetails({ details }) {
  return (
    <div className="space-y-2">
      {details.designation && (
        <p className="text-xs text-stone-400">{details.designation}</p>
      )}
      {details.description && (
        <p className="text-sm text-stone-600 leading-relaxed">{details.description}</p>
      )}
      {details.operator && (
        <p className="text-xs text-stone-400">Managed by: {details.operator}</p>
      )}
      {(details.url || details.website) && (
        <a
          href={details.url || details.website}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-sm text-[#2d3b2d] font-medium hover:underline"
        >
          {details.source === 'nps' ? 'Visit NPS page' : 'More info'}
        </a>
      )}
    </div>
  );
}

function TrailDetails({ details }) {
  return (
    <div className="space-y-2">
      {details.surface && (
        <div className="flex items-center justify-between bg-amber-50 rounded-xl px-3 py-2">
          <span className="text-sm text-amber-700">Surface</span>
          <span className="text-sm font-semibold text-amber-800 capitalize">{details.surface}</span>
        </div>
      )}
      {details.operator && (
        <p className="text-xs text-stone-400">Managed by: {details.operator}</p>
      )}
    </div>
  );
}

function HikeDetails({ details }) {
  return (
    <div className="space-y-2">
      {details.lengthMi != null && (
        <div className="flex items-center justify-between bg-orange-50 rounded-xl px-3 py-2">
          <span className="text-sm text-orange-700">Length</span>
          <span className="text-sm font-semibold text-orange-800">{details.lengthMi.toFixed(1)} mi</span>
        </div>
      )}
      {details.difficulty && (
        <div className="flex items-center justify-between bg-orange-50 rounded-xl px-3 py-2">
          <span className="text-sm text-orange-700">Difficulty</span>
          <span className="text-sm font-semibold text-orange-800 capitalize">{details.difficulty}</span>
        </div>
      )}
      {details.description && (
        <p className="text-sm text-stone-600 leading-relaxed">{details.description}</p>
      )}
    </div>
  );
}

function FeatureDetails({ details }) {
  return (
    <div className="space-y-2">
      {details.ele && (
        <div className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2">
          <span className="text-sm text-stone-600">Elevation</span>
          <span className="text-sm font-semibold text-stone-700">{details.ele}</span>
        </div>
      )}
      {details.height && (
        <div className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2">
          <span className="text-sm text-stone-600">Height</span>
          <span className="text-sm font-semibold text-stone-700">{details.height}</span>
        </div>
      )}
      {details.description && (
        <p className="text-sm text-stone-600 leading-relaxed">{details.description}</p>
      )}
    </div>
  );
}
